import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  TrashIcon,
  PencilIcon,
  PlusIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useGalleries } from '../../contexts/GalleryContext';

// 플레이스홀더 이미지 URL
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=800&h=600&fit=crop&q=80';

const GalleryList: React.FC = () => {
  const { galleries, deleteGallery } = useGalleries();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredGalleries = galleries.filter(gallery => {
    // 검색어 필터
    const matchesSearch = searchTerm === '' ||
      gallery.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (gallery.description && gallery.description.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  const handleDelete = (id: number) => {
    if (window.confirm('정말로 이 갤러리 항목을 삭제하시겠습니까?')) {
      deleteGallery(id);
      alert('갤러리 항목이 삭제되었습니다.');
    }
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">갤러리 관리</h1>
          <p className="text-gray-600">갤러리 이미지를 등록하고 관리합니다.</p>
        </div>
        <Link
          to="/admin/gallery/new"
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          새 갤러리 등록
        </Link>
      </div>

      {/* 검색 */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="제목 또는 설명으로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>

      {/* 갤러리 통계 */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-600">전체</p>
            <p className="text-2xl font-bold text-gray-800">{galleries.length}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">메인 노출</p>
            <p className="text-2xl font-bold text-green-600">
              {galleries.filter(g => g.is_featured).length}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">검색 결과</p>
            <p className="text-2xl font-bold text-blue-600">{filteredGalleries.length}</p>
          </div>
        </div>
      </div>

      {/* 갤러리 그리드 */}
      <div className="bg-white rounded-lg shadow p-6">
        {filteredGalleries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">등록된 갤러리가 없습니다.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredGalleries.map((gallery) => {
              return (
                <div
                  key={gallery.id}
                  className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all"
                >
                  {/* 이미지 */}
                  <div className="relative h-48 bg-gray-200 overflow-hidden">
                    <img
                      src={gallery.thumbnail_url || gallery.image_url || PLACEHOLDER_IMAGE}
                      alt={gallery.title}
                      className="w-full h-full object-cover"
                    />

                    {/* 메인 노출 아이콘 */}
                    {gallery.is_featured && (
                      <div className="absolute top-2 right-2">
                        <StarSolidIcon className="h-6 w-6 text-yellow-400 drop-shadow-lg" />
                      </div>
                    )}
                  </div>

                  {/* 내용 */}
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-gray-800 mb-2 line-clamp-1">
                      {gallery.title}
                    </h3>

                    {gallery.description && (
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                        {gallery.description}
                      </p>
                    )}

                    {/* 통계 */}
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                      <span>ID: {gallery.id}</span>
                    </div>

                    <div className="text-xs text-gray-400 mb-4">
                      {new Date(gallery.created_at).toLocaleDateString('ko-KR')}
                    </div>

                    {/* 액션 버튼 */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/admin/gallery/edit/${gallery.id}`)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" />
                        수정
                      </button>
                      <button
                        onClick={() => handleDelete(gallery.id)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                        삭제
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryList;
